import type {
  ClinicDocumentStatusResponse,
  ClinicDocumentUploadResponse,
} from '@store/generated/clinic-documents';
import { enhancedApi as clinicDocumentsGeneratedApi } from '@store/generated/clinic-documents';

/**
 * Typed shape of extracted_data for fee_schedule task results.
 * The generated type is { [key: string]: any } — we cast to this.
 */
export type FeeScheduleExtractedData = {
  payer_name?: string;
  effective_date?: string;
  confidence?: number;
  rows?: Array<{
    code_id: string;
    charge_cents: number;
    modifiers?: Record<string, unknown>;
  }>;
};

/**
 * Clinic Documents API — enhanced with fee schedule extraction support.
 *
 * Injects a custom upload mutation (FormData with extraction_tasks=fee_schedule)
 * alongside the existing generated endpoints.
 *
 * Extraction result polling uses the generated
 * getV2ProviderClinicDocumentsByClinicDocumentIdExtractionResultsAndTaskName
 * endpoint (re-exported below as useGetFeeScheduleExtractionResultQuery).
 */
export const feeScheduleDocumentsApi = clinicDocumentsGeneratedApi.injectEndpoints({
  endpoints: builder => ({
    uploadFeeScheduleDocument: builder.mutation<
      ClinicDocumentUploadResponse,
      { file: File; scopeId?: string }
    >({
      query: ({ file, scopeId }) => {
        const formData = new FormData();
        formData.append('files[]', file, file.name);
        formData.append('scope_type', 'organization');
        formData.append('extraction_tasks', 'fee_schedule');
        if (scopeId) {
          formData.append('scope_id', scopeId);
        }
        return {
          url: '/v2/provider/clinic-documents',
          method: 'POST',
          body: formData,
        };
      },
    }),

    getFeeScheduleDocumentStatus: builder.query<
      ClinicDocumentStatusResponse,
      { clinicDocumentId: string }
    >({
      query: ({ clinicDocumentId }) => ({
        url: `/v2/provider/clinic-documents/${clinicDocumentId}/status`,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useUploadFeeScheduleDocumentMutation,
  useGetFeeScheduleDocumentStatusQuery,
  // Generated endpoint — re-exported with a readable name
  useGetV2ProviderClinicDocumentsByClinicDocumentIdExtractionResultsAndTaskNameQuery:
    useGetFeeScheduleExtractionResultQuery,
} = feeScheduleDocumentsApi;
